import { useRef, useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import Modal from "../modal/Modal";
import CountryMenuItem from "./CountryMenuItem";
import useTranslation from "../../hooks/useTranslation";
import { TextControl } from "../generic/Core";


const ListGroup = styled.ul`
    --list-group-color: ${({ theme }) => theme.font.color};
    --list-group-bg: ${({ theme }) => theme.dropdown.background};
    --list-group-border-color: ${({ theme }) => theme.dropdown.border};
    --list-group-border-width: 1px;
    --list-group-border-radius: 0.375rem;
    --list-group-item-padding-x: 1rem;
    --list-group-item-padding-y: 0.5rem;
    --list-group-action-color: ${({ theme }) => theme.font.color};
    --list-group-action-hover-color: ${({ theme }) => theme.dropdown.selectedFont};
    --list-group-action-hover-bg: ${({ theme }) => theme.dropdown.selectedBackground};
    --list-group-action-active-color: ${({ theme }) => theme.dropdown.selectedFont};
    --list-group-action-active-bg: ${({ theme }) => theme.dropdown.selectedBackground};
    --list-group-disabled-color: ${({ theme }) => theme.font.color};
    --list-group-disabled-bg: ${({ theme }) => theme.dropdown.background};
    --list-group-active-color: ${({ theme }) => theme.dropdown.selectedFont};
    --list-group-active-bg: ${({ theme }) => theme.dropdown.selectedBackground};
    --list-group-active-border-color: ${({ theme }) => theme.dropdown.selectedBackground};
    display: flex;
    flex-direction: column;
    background-color: var(--list-group-bg);
    padding-left: 0;
    margin-bottom: 0;
    border-radius: var(--list-group-border-radius);
    overflow-x: hidden;
`

const SearchResults = styled.div`
    position: absolute;
    top: 60px;
    width: calc(100% - 20px);
    max-height: calc(100% - 60px);
    overflow-y: auto;
    z-index:101;

    &.visible {
      visibility: visible;
    }

    &.invisible {
        visibility: hidden!important;
    }
`;

const Jumper = ({ headers }) => {
    const scroll = (id) => {
        const elem = document.getElementById(id)
        if (elem) { elem.scrollIntoView({ behavior: 'smooth' }) }
    }

    return (
        <JumpWrapper>
            {headers.map((h) => (
                <JumpItem key={h.id} onClick={() => scroll(`${h.id}`)}>{h.name}</JumpItem>
            ))}
        </JumpWrapper>
    )
}

const JumpWrapper = styled.div`
    position: absolute;
    width:10px;
    height: calc(100% - 60px);
    right: 10px;
    top: 60px;
    z-index:2000;
`

const JumpItem = styled.div`
    font-size: calc(1.8vh);
`


const CountryModal_m = ({
    data,
    valueName,
    isVisible,
    setIsVisible,
    onSelect,
    flags
}) => {
    const { t } = useTranslation()
    const [cursor, setCursor] = useState(-1);
    const [search, setSearch] = useState("");

    const searchResultRef = useRef(null);
    const focusRef = useRef(null)

    const scrollIntoView = position => {
        searchResultRef.current.parentNode.scrollTo({
            top: position,
            behavior: "smooth"
        });
    };

    // Auto-focus the search field when the modal opens
    useEffect(() => {
        focusRef.current.focus();
    }, [])

    useEffect(() => {
        if (cursor < 0 || cursor > suggestions.length || !searchResultRef) {
            return () => { };
        }

        let listItems = Array.from(searchResultRef.current.children);
        listItems[cursor] && scrollIntoView(listItems[cursor].offsetTop);
    }, [cursor]);

    // Clear search when the selected value changes externally
    useEffect(() => {
        setSearch('')
    }, [valueName])

    const suggestions = useMemo(() => {
        if (!search) return data.list;
        setCursor(-1);
        scrollIntoView(0);

        const searchLower = search.toLowerCase();
        return data.list.filter(item =>
            (searchLower.length >= 3 && item.name.toLowerCase().includes(searchLower)) ||
            item.id.toLowerCase().includes(searchLower)
        );
    }, [data.list, search]);

    const keyboardNavigation = e => {
        if (e.key === "ArrowDown") {
            setCursor(c => (c < suggestions.length - 1 ? c + 1 : c));
        }

        if (e.key === "ArrowUp") {
            setCursor(c => (c > 0 ? c - 1 : 0));
        }

        if (e.key === "Escape") {
            setIsVisible(false);
        }

        if (e.key === "Enter" && cursor >= 0) {
            setSearch(suggestions[cursor].name);
            setIsVisible(false);
            onSelect(suggestions[cursor]);
        }
    };

    return (
        <Modal setShow={setIsVisible} show={isVisible} mode={'fullscreen'} className={'fullscreen'}>
            <Modal.Header onClose={() => setIsVisible(false)}>
                <h3 style={{ margin: '0px' }}>{t('country.modal.title')}</h3>
            </Modal.Header>
            <Modal.Body>
                <TextControl
                    type="text"
                    value={search || ''}
                    name={'name'}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => keyboardNavigation(e)}
                    autoComplete="new-country"
                    ref={focusRef}
                />
                <SearchResults>
                    <ListGroup ref={searchResultRef} id='countries-menu'>
                        {suggestions.map((item, idx) => (
                            <CountryMenuItem
                                key={item.id}
                                onSelectItem={() => {
                                    setIsVisible(false);
                                    setSearch(item.name);
                                    onSelect(item);
                                }}
                                isHighlighted={cursor === idx}
                                {...item}
                                flag={flags[item.id]}
                            />
                        ))}
                    </ListGroup>
                </SearchResults>
                <Jumper headers={data.headers} />
            </Modal.Body>
        </Modal>
    );
};

export default CountryModal_m;
